import { useState, useEffect } from 'react';

/**
 * SSR-safe drop-in replacement for beautiful-react-hooks' useLocalStorage.
 * Same JSON.stringify/parse behavior, but silently returns the default during SSR
 * instead of spamming console.warn on every render.
 */
export default function useLocalStorageSafe<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const isClient = typeof window !== 'undefined';

  const [value, setValue] = useState<T>(() => {
    if (!isClient) return defaultValue;
    try {
      const stored = window.localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage full or blocked — silently ignore
    }
  }, [key, value]);

  return [value, setValue];
}
